import { isEditorHtmlEmpty } from '@utils/draftjs';

import { careerLanguageInSentenceMap } from 'locale';
import type { JobLanguage } from 'types/jobs';

export type Translate = (key: string, ...args: unknown[]) => string;

type PublishableLanguage = Pick<
  JobLanguage,
  'language' | 'isEnabled' | 'title' | 'shortDescriptionHtml'
> & {
  form?: { sections?: { questions?: { title?: string; hidden?: boolean }[] }[] } | null;
};

/** What a language still needs before the job can be published with it enabled. */
export const jobLanguageGaps = (language: PublishableLanguage) => ({
  title: !language.title,
  shortDescription: isEditorHtmlEmpty(language.shortDescriptionHtml),
  form: (language.form?.sections ?? []).some((section) =>
    (section.questions ?? []).some((question) => !question.title && !question.hidden),
  ),
});

export const jobLanguageIncompleteMessage = (language: PublishableLanguage, t: Translate) => {
  const gaps = jobLanguageGaps(language);
  const reasons = [
    gaps.title && t('job.create.languages.missing.title'),
    gaps.shortDescription && t('job.create.languages.missing.short'),
    gaps.form && t('job.create.languages.missing.form'),
  ].filter(Boolean);

  if (reasons.length === 0) return null;

  return t(
    'job.create.languages.publish.incomplete',
    t(careerLanguageInSentenceMap[language.language]),
    reasons.join(', '),
  );
};

/** One message per reason the job can't be published yet; empty when it can. */
export const jobPublishBlockers = (languages: PublishableLanguage[], t: Translate): string[] => {
  const enabled = languages.filter((language) => language.isEnabled);
  if (enabled.length === 0) return [t('job.create.publish.no_enabled_language')];

  return enabled
    .map((language) => jobLanguageIncompleteMessage(language, t))
    .filter((message): message is string => message !== null);
};
